import { COMPARE_IMAGE_WITH_DESCRIPTION_MAX_ANSWER_ATTEMPTS } from "../CompareImageWithDescription.helpers";
import {
  TCompareImageWithDescriptionExerciseParsedAnswer,
  TCompareImageWithDescriptionExerciseParsedImage,
  TExerciseIdentifier,
} from "types/app/exercises";

/**
 * Helper function that allows to update answer information on user input enter.
 * @param answers an array of answers that should be updated.
 * @param targetAnswer target answer data that should be updated.
 * @returns updated answers data.
 */
export const updateAnswersDataOnInputEnter = (
  answers: TCompareImageWithDescriptionExerciseParsedAnswer[],
  targetAnswer: TCompareImageWithDescriptionExerciseParsedAnswer
): TCompareImageWithDescriptionExerciseParsedAnswer[] =>
  answers.map((answer) =>
    answer.id === targetAnswer.id
      ? {
          ...answer,
          isSelected: true,
          isTouched: true,
        }
      : answer
  );

/**
 * Helper function that allows to update answer information on user input leave.
 * @param answers an array of answers that should be updated.
 * @param targetAnswer target answer data that should be updated.
 * @returns updated answers data.
 */
export const updateAnswersDataOnInputLeave = (
  answers: TCompareImageWithDescriptionExerciseParsedAnswer[],
  targetAnswer: TCompareImageWithDescriptionExerciseParsedAnswer
): TCompareImageWithDescriptionExerciseParsedAnswer[] =>
  answers.map((answer) =>
    answer.id === targetAnswer.id
      ? {
          ...answer,
          isSelected: false,
        }
      : answer
  );

/**
 * Helper function that allows to update image information on user input enter.
 * @param images an array of images that should be updated.
 * @param targetImageId target image id that should be updated.
 * @param selectedAnswer target answer data that should be associated with the image.
 * @returns updated images data.
 */
export const updateImagesDataOnInputEnter = (
  images: TCompareImageWithDescriptionExerciseParsedImage[],
  targetImageId: TExerciseIdentifier,
  selectedAnswer: TCompareImageWithDescriptionExerciseParsedAnswer
): TCompareImageWithDescriptionExerciseParsedImage[] =>
  images.map((image) =>
    image.id === targetImageId
      ? {
          ...image,
          attempts: [...image.attempts, selectedAnswer],
          currentAnswer: selectedAnswer,
          currentAttempt: image.currentAttempt + 1,
          isCorrectAnswer: image.correctAnswers.some(
            (correctAnswer) => correctAnswer.id === selectedAnswer.id
          ),
          isEmpty: false,
          isTouched: true,
          isAllAttemptsFailed:
            image.correctAnswers.some(
              (correctAnswer) => correctAnswer.id === selectedAnswer.id
            ) &&
            image.currentAttempt + 1 >=
              COMPARE_IMAGE_WITH_DESCRIPTION_MAX_ANSWER_ATTEMPTS,
        }
      : image
  );

/**
 * Helper function that allows to update image information on user input leave.
 * @param images an array of images that should be updated.
 * @param targetImageId target image id that should be updated.
 * @returns updated images data.
 */
export const updateImagesDataOnInputLeave = (
  images: TCompareImageWithDescriptionExerciseParsedImage[],
  targetImageId: TExerciseIdentifier
): TCompareImageWithDescriptionExerciseParsedImage[] =>
  images.map((image) =>
    image.id === targetImageId
      ? {
          ...image,
          currentAnswer: null,
          isCorrectAnswer: false,
          isEmpty: true,
        }
      : image
  );
